import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Button,
    Slider,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface AudioRecorderModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (audioBlob: Blob, duration: number) => void;
}

type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped' | 'playing';

const AudioRecorderModal: React.FC<AudioRecorderModalProps> = ({
    open,
    onClose,
    onSave,
}) => {
    const theme = useTheme();
    const [recordingState, setRecordingState] = useState<RecordingState>('idle');
    const [duration, setDuration] = useState(0);
    const [playbackPosition, setPlaybackPosition] = useState(0);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const audioUrlRef = useRef<string | null>(null);

    useEffect(() => {
        return () => {
            // Cleanup on unmount
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            if (audioUrlRef.current) {
                URL.revokeObjectURL(audioUrlRef.current);
            }
            if (audioElementRef.current) {
                audioElementRef.current.pause();
            }
        };
    }, []);

    const formatTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const startTimer = () => {
        timerRef.current = setInterval(() => {
            setDuration(prev => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                const audioUrl = URL.createObjectURL(audioBlob);
                audioUrlRef.current = audioUrl;

                // Create audio element for playback
                const audio = new Audio(audioUrl);
                audioElementRef.current = audio;

                audio.ontimeupdate = () => {
                    setPlaybackPosition(audio.currentTime);
                };

                audio.onended = () => {
                    setRecordingState('stopped');
                    setPlaybackPosition(0);
                };
            };

            mediaRecorder.start();
            setRecordingState('recording');
            setDuration(0);
            startTimer();
        } catch (error) {
            console.error('Error starting recording:', error);
        }
    };

    const pauseRecording = () => {
        if (mediaRecorderRef.current && recordingState === 'recording') {
            mediaRecorderRef.current.pause();
            setRecordingState('paused');
            stopTimer();
        }
    };

    const resumeRecording = () => {
        if (mediaRecorderRef.current && recordingState === 'paused') {
            mediaRecorderRef.current.resume();
            setRecordingState('recording');
            startTimer();
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            setRecordingState('stopped');
            stopTimer();
        }
    };

    const playAudio = () => {
        if (audioElementRef.current && recordingState === 'stopped') {
            audioElementRef.current.play();
            setRecordingState('playing');
        }
    };

    const pauseAudio = () => {
        if (audioElementRef.current && recordingState === 'playing') {
            audioElementRef.current.pause();
            setRecordingState('stopped');
        }
    };

    const restartAudio = () => {
        if (audioElementRef.current) {
            audioElementRef.current.currentTime = 0;
            setPlaybackPosition(0);
            if (recordingState === 'playing') {
                audioElementRef.current.play();
            }
        }
    };

    const deleteRecording = () => {
        if (audioElementRef.current) {
            audioElementRef.current.pause();
        }
        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
        setRecordingState('idle');
        setDuration(0);
        setPlaybackPosition(0);
        audioChunksRef.current = [];
    };

    const handleSave = () => {
        if (audioChunksRef.current.length > 0) {
            const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
            onSave(audioBlob, duration);
            onClose();
            // Reset state
            setRecordingState('idle');
            setDuration(0);
            setPlaybackPosition(0);
        }
    };

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        const value = newValue as number;
        if (audioElementRef.current) {
            audioElementRef.current.currentTime = value;
            setPlaybackPosition(value);
        }
    };

    const renderRecordingControls = () => {
        switch (recordingState) {
            case 'idle':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {formatTime(0)}
                        </Typography>
                        <IconButton
                            onClick={startRecording}
                            sx={{
                                bgcolor: '#f44336',
                                color: 'white',
                                width: 50,
                                height: 50,
                                '&:hover': { bgcolor: '#f44336' },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 15,
                                    height: 15,
                                    borderRadius: '50%',
                                    bgcolor: 'white',
                                }}
                            />
                        </IconButton>
                        <Button
                            variant="contained"
                            disabled
                            sx={{
                                bgcolor: '#9e9e9e',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                        >
                            Opslaan
                        </Button>
                    </Box>
                );

            case 'recording':
            case 'paused':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {formatTime(duration)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <IconButton
                                onClick={recordingState === 'recording' ? pauseRecording : resumeRecording}
                                sx={{
                                    bgcolor: theme.palette.grey[300],
                                    width: 50,
                                    height: 50,
                                    '&:hover': { bgcolor: theme.palette.grey[300] },
                                }}
                            >
                                {recordingState === 'recording' ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                            <IconButton
                                onClick={stopRecording}
                                sx={{
                                    bgcolor: '#f44336',
                                    color: 'white',
                                    width: 50,
                                    height: 50,
                                    '&:hover': { bgcolor: '#f44336' },
                                }}
                            >
                                <StopIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            <IconButton
                                onClick={deleteRecording}
                                sx={{
                                    bgcolor: theme.palette.grey[300],
                                    width: 50,
                                    height: 50,
                                    '&:hover': { bgcolor: theme.palette.grey[300] },
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                        <Button
                            variant="contained"
                            disabled
                            sx={{
                                bgcolor: '#9e9e9e',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                fontSize: 14,
                            }}
                        >
                            Opslaan
                        </Button>
                    </Box>
                );

            case 'stopped':
            case 'playing':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box sx={{ width: '100%', px: 1 }}>
                            <Slider
                                value={playbackPosition}
                                max={duration}
                                onChange={handleSliderChange}
                                sx={{
                                    color: theme.palette.primary.main,
                                    height: 7,
                                    '& .MuiSlider-thumb': {
                                        width: 17,
                                        height: 17,
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <IconButton
                                onClick={restartAudio}
                                sx={{
                                    bgcolor: theme.palette.grey[300],
                                    width: 48,
                                    height: 48,
                                    '&:hover': { bgcolor: theme.palette.grey[300] },
                                }}
                            >
                                <RestartAltIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            <IconButton
                                onClick={recordingState === 'playing' ? pauseAudio : playAudio}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: 'white',
                                    width: 48,
                                    height: 48,
                                    '&:hover': { bgcolor: theme.palette.primary.main },
                                }}
                            >
                                {recordingState === 'playing' ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                            <IconButton
                                onClick={deleteRecording}
                                sx={{
                                    bgcolor: theme.palette.grey[300],
                                    width: 48,
                                    height: 48,
                                    '&:hover': { bgcolor: theme.palette.grey[300] },
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                fontSize: 14,
                                '&:hover': { bgcolor: theme.palette.primary.main },
                            }}
                        >
                            Opslaan
                        </Button>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 2,
                    minHeight: 0,
                    width: 250,
                    maxWidth: '90vw',
                },
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 4,
                    top: 4,
                    color: theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ pt: 2, pb: 1, px: 0.5 }}>
                <Typography
                    variant="h6"
                    sx={{
                        textAlign: 'center',
                        mb: 1,
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: 16,
                    }}
                >
                    Neem audio op
                </Typography>

                {renderRecordingControls()}
            </DialogContent>
        </Dialog>
    );
};

export default AudioRecorderModal;
